import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Zap, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { Modal } from '../components/Modal';

interface BerthRow {
  id: number;
  label: string;
  number: string | null;
  dock_zone: string | null;
  max_length_m: number | null;
  max_beam_m: number | null;
  max_draft_m: number | null;
  has_electricity: boolean;
  berth_type: string;
  is_active: boolean;
  notes: string | null;
}

interface BerthForm {
  label: string;
  number: string;
  dock_zone: string;
  max_length_m: string;
  max_beam_m: string;
  max_draft_m: string;
  has_electricity: boolean;
  berth_type: string;
  notes: string;
}

const EMPTY_FORM: BerthForm = {
  label: '', number: '', dock_zone: '', max_length_m: '', max_beam_m: '', max_draft_m: '',
  has_electricity: false, berth_type: 'visitor', notes: '',
};

export function BerthsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const [berths, setBerths] = useState<BerthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editBerth, setEditBerth] = useState<BerthRow | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BerthRow | null>(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<BerthRow | null>(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchBerths = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getBerths();
      setBerths(data.berths as unknown as BerthRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => { fetchBerths(); }, [fetchBerths]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const result = (await api.deleteBerth(deleteTarget.id)) as { warning?: string } | undefined;
      toast.success(t('berths.berthDeactivated', { label: deleteTarget.label }));
      if (result?.warning) toast.info(result.warning);
      setDeleteTarget(null);
      fetchBerths();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    try {
      await api.deleteBerth(permanentDeleteTarget.id, { permanent: true });
      toast.success(t('berths.permanentDeleted', { label: permanentDeleteTarget.label }));
      setPermanentDeleteTarget(null);
      fetchBerths();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'BERTH_HAS_BOOKINGS') toast.error(t('berths.permanentDeleteBlockedBookings'));
        else if (err.code === 'MUST_DEACTIVATE') toast.error(t('berths.permanentDeleteMustDeactivate'));
        else toast.error(err.message);
      } else {
        toast.error(t('errors.somethingWentWrong'));
      }
    }
  };

  const activeBerths = berths.filter((b) => b.is_active);
  const inactiveBerths = berths.filter((b) => !b.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('berths.title')}</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> {t('berths.addBerth')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {activeBerths.length === 0 ? (
          <div className="px-5 py-16 text-center text-sm text-gray-400">
            {t('berths.noBerths')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('berths.label')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">{t('berths.zone')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('berths.maxLength')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">{t('berths.maxBeam')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">{t('berths.maxDraft')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('berths.type')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('berths.elec')}</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeBerths.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900">{b.label}</td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{b.dock_zone ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{b.max_length_m ? `${b.max_length_m}m` : '—'}</td>
                    <td className="px-4 py-3 text-gray-700 hidden md:table-cell">{b.max_beam_m ? `${b.max_beam_m}m` : '—'}</td>
                    <td className="px-4 py-3 text-gray-700 hidden md:table-cell">{b.max_draft_m ? `${b.max_draft_m}m` : '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{b.berth_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      {b.has_electricity && <Zap className="w-4 h-4 text-amber-500" />}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditBerth(b)} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteTarget(b)} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inactiveBerths.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('berths.inactiveBerths')}</h3>
          <div className="space-y-2">
            {inactiveBerths.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 text-sm text-gray-600">
                <span className="min-w-0 truncate">{b.label} ({b.dock_zone ?? t('berths.noZone')})</span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.updateBerth(b.id, { is_active: true });
                        toast.success(t('berths.berthReactivated', { label: b.label }));
                        fetchBerths();
                      } catch (err) {
                        toast.error(err instanceof ApiError ? err.message : t('berths.failedToReactivate'));
                      }
                    }}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {t('berths.reactivate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPermanentDeleteTarget(b)}
                    className="text-xs text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('berths.deletePermanently')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      <BerthFormModal
        open={showAdd || !!editBerth}
        onClose={() => { setShowAdd(false); setEditBerth(null); }}
        berth={editBerth}
        onSaved={() => { setShowAdd(false); setEditBerth(null); fetchBerths(); }}
      />

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('berths.deactivateBerth')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                {t('berths.deactivateBerth')} <span className="font-semibold">{deleteTarget?.label}</span>?
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('berths.deactivateWarning')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button type="button" onClick={() => { void handleDelete(); }} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
              {t('berths.deactivate')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!permanentDeleteTarget}
        onClose={() => setPermanentDeleteTarget(null)}
        title={t('berths.permanentDeleteTitle')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">{t('berths.permanentDeleteBody', { label: permanentDeleteTarget?.label ?? '' })}</p>
              <p className="text-xs text-gray-500 mt-2">{t('berths.permanentDeleteHint')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('common.thisActionCannotBeUndone')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPermanentDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button type="button" onClick={() => { void handlePermanentDelete(); }} className="flex-1 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800">
              {t('berths.deletePermanently')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Berth Form Modal ──────────────────────────────────────────────────

function BerthFormModal({
  open, onClose, berth, onSaved,
}: { open: boolean; onClose: () => void; berth: BerthRow | null; onSaved: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<BerthForm>(EMPTY_FORM);

  useEffect(() => {
    if (berth) {
      setForm({
        label: berth.label,
        number: berth.number ?? '',
        dock_zone: berth.dock_zone ?? '',
        max_length_m: berth.max_length_m?.toString() ?? '',
        max_beam_m: berth.max_beam_m?.toString() ?? '',
        max_draft_m: berth.max_draft_m?.toString() ?? '',
        has_electricity: berth.has_electricity,
        berth_type: berth.berth_type,
        notes: berth.notes ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [berth, open]);

  const set = (key: string, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        label: form.label,
        number: form.number || form.label.replace(/^[A-Za-z]+-?/, '') || form.label,
        dock_zone: form.dock_zone || null,
        max_length_m: form.max_length_m ? parseFloat(form.max_length_m) : null,
        max_beam_m: form.max_beam_m ? parseFloat(form.max_beam_m) : null,
        max_draft_m: form.max_draft_m ? parseFloat(form.max_draft_m) : null,
        has_electricity: form.has_electricity,
        berth_type: form.berth_type,
        notes: form.notes || null,
      };

      if (berth) {
        await api.updateBerth(berth.id, payload);
        toast.success(t('berths.berthUpdated', { label: form.label }));
      } else {
        await api.createBerth(payload);
        toast.success(t('berths.berthCreated', { label: form.label }));
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <Modal open={open} onClose={onClose} title={berth ? t('berths.editBerthTitle', { label: berth.label }) : t('berths.addBerthTitle')}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>{t('berths.berthLabel')}</label>
            <input type="text" value={form.label} onChange={(e) => set('label', e.target.value)} required maxLength={20} placeholder="e.g. A-1" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('berths.berthNumber')}</label>
            <input type="text" value={form.number} onChange={(e) => set('number', e.target.value)} maxLength={20} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('berths.dockZone')}</label>
            <input type="text" value={form.dock_zone} onChange={(e) => set('dock_zone', e.target.value)} maxLength={50} className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>{t('berths.maxLengthM')}</label>
            <input type="number" step="0.1" min="1" max="100" value={form.max_length_m} onChange={(e) => set('max_length_m', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('berths.maxBeamM')}</label>
            <input type="number" step="0.1" min="0.5" max="30" value={form.max_beam_m} onChange={(e) => set('max_beam_m', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('berths.maxDraftM')}</label>
            <input type="number" step="0.1" min="0.1" max="20" value={form.max_draft_m} onChange={(e) => set('max_draft_m', e.target.value)} className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>{t('berths.berthType')}</label>
            <select value={form.berth_type} onChange={(e) => set('berth_type', e.target.value)} className={inputClass}>
              <option value="visitor">{t('berths.typeVisitor')}</option>
              <option value="seasonal">{t('berths.typeSeasonal')}</option>
              <option value="both">{t('berths.typeBoth')}</option>
            </select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.has_electricity} onChange={(e) => set('has_electricity', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
              <span className="text-sm text-gray-700">{t('berths.hasElectricity')}</span>
            </label>
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('common.notes')}</label>
          <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} maxLength={500} className={inputClass} />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {submitting ? t('common.saving') : berth ? t('berths.saveChanges') : t('berths.createBerth')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
